/* Variant Availability Manager
 * Handles dynamic showing/hiding of unavailable variant options
 * Distinguishes between sold-out and unavailable states
 */

(function () {
  "use strict";

  const selectors = {
    productSection: ".product.section",
    productForm: "[data-product-form]",
    optionSelector: "[data-product-option-selector]",
    masterSelect: "[data-product-master-select]",
    productJSON: "[data-product-json]",
    productMeta: ".product__meta",
    swatch: ".js-color-swatch",
    swatchGrid: ".js-color-swatch-grid",
    swatchElement: ".swatch-element",
    optionLabel: "[data-product-option-label]",
    variantOptionLabel: ".variant-option-label",
    productOption: "[data-product-option]",
  };

  const classes = {
    soldOut: "variant-option-label--sold-out",
    unavailable: "variant-option-label--unavailable",
    swatchSoldOut: "swatch-element--sold-out",
    swatchUnavailable: "swatch-element--unavailable",
    disabled: "disabled",
  };

  const states = {
    AVAILABLE: "available",
    SOLD_OUT: "sold-out",
    UNAVAILABLE: "unavailable",
  };

  class VariantAvailability {
    constructor(container) {
      this.container = container;
      this.productJSON = this.getProductJSON();

      if (!this.productJSON || !this.productJSON.variants) {
        console.warn("Product JSON not found or invalid");
        return;
      }

      this.init();
    }

    init() {
      // Listen for option changes
      this.container.addEventListener("change", (event) => {
        const target = event.target;
        if (
          target.matches(selectors.optionSelector) ||
          target.matches(selectors.swatch) ||
          target.matches(selectors.swatchGrid)
        ) {
          this.updateAvailability();
        }
      });

      // Listen for variant changes from theme
      this.container.addEventListener("variantChange", () => {
        this.updateAvailability();
      });

      // Initial update
      setTimeout(() => {
        this.updateAvailability();
      }, 100);
    }

    getProductJSON() {
      const jsonElement = this.container.querySelector(selectors.productJSON);
      if (!jsonElement) return null;

      try {
        return JSON.parse(jsonElement.innerHTML);
      } catch (e) {
        console.error("Error parsing product JSON:", e);
        return null;
      }
    }

    getCurrentSelections() {
      const selections = {};

      // Get selections from regular option selectors
      this.container
        .querySelectorAll(selectors.optionSelector)
        .forEach((selector) => {
          const index = selector.dataset.index; // 'option1', 'option2', etc.

          if (selector.type === "radio") {
            if (selector.checked) {
              selections[index] = selector.value;
            }
          } else if (selector.tagName === "SELECT") {
            selections[index] = selector.value;
          }
        });

      // Get selections from swatches
      this.container.querySelectorAll(selectors.swatch).forEach((swatch) => {
        if (swatch.checked) {
          const index = swatch.dataset.optionPosition;
          selections[index] = swatch.value;
        }
      });

      // Get selections from grid swatches
      this.container
        .querySelectorAll(selectors.swatchGrid)
        .forEach((swatch) => {
          if (swatch.checked) {
            const index = swatch.dataset.optionPosition;
            selections[index] = swatch.value;
          }
        });

      return selections;
    }

    checkOptionAvailability(optionValue, optionPosition, currentSelections) {
      let hasVariant = false;
      let isAvailable = false;

      for (const variant of this.productJSON.variants) {
        const variantOptionValue = variant[optionPosition];

        // Check if this variant has the option value we're checking
        if (variantOptionValue !== optionValue) {
          continue;
        }

        // Check if it matches other selected options
        let matches = true;
        ["option1", "option2", "option3"].forEach((opt) => {
          if (opt !== optionPosition && currentSelections[opt]) {
            if (variant[opt] !== currentSelections[opt]) {
              matches = false;
            }
          }
        });

        if (matches) {
          hasVariant = true;
          if (variant.available) {
            isAvailable = true;
            break; // Found an available variant, no need to continue
          }
        }
      }

      if (isAvailable) return states.AVAILABLE;
      if (hasVariant) return states.SOLD_OUT;
      return states.UNAVAILABLE;
    }

    updateAvailability() {
      const currentSelections = this.getCurrentSelections();

      // Update radio buttons
      this.updateRadioButtons(currentSelections);

      // Update select dropdowns
      this.updateSelectOptions(currentSelections);

      // Update swatches
      this.updateSwatches(currentSelections);

      // Update grid swatches
      this.updateGridSwatches(currentSelections);
    }

    updateRadioButtons(currentSelections) {
      this.container
        .querySelectorAll(selectors.productOption)
        .forEach((optionContainer, index) => {
          const radios = optionContainer.querySelectorAll(
            'input[type="radio"]' + selectors.optionSelector,
          );
          if (radios.length === 0) return;

          const optionPosition = `option${index + 1}`;

          radios.forEach((radio) => {
            const value = radio.value;
            const state = this.checkOptionAvailability(
              value,
              optionPosition,
              currentSelections,
            );

            radio.dataset.availabilityState = state;

            const label = optionContainer.querySelector(
              `label[for="${radio.id}"]`,
            );
            if (!label) return;

            // Remove all state classes
            label.classList.remove(classes.soldOut, classes.unavailable);

            // Remove existing badge
            const existingBadge = label.querySelector(
              ".variant-availability-badge",
            );
            if (existingBadge) {
              existingBadge.remove();
            }

            // Add state-specific styling
            if (state === states.SOLD_OUT) {
              label.classList.add(classes.soldOut);
              const badge = document.createElement("span");
              badge.className = "variant-availability-badge";
              badge.textContent =
                window.themeVariables?.strings?.soldOutButton || "Sold Out";
              label.appendChild(badge);
            } else if (state === states.UNAVAILABLE) {
              label.classList.add(classes.unavailable);
              const badge = document.createElement("span");
              badge.className = "variant-availability-badge";
              badge.textContent = "Unavailable";
              label.appendChild(badge);
            }

            // Update label data attribute
            label.dataset.availabilityState = state;
          });
        });
    }

    updateSelectOptions(currentSelections) {
      this.container
        .querySelectorAll(selectors.productOption)
        .forEach((optionContainer, index) => {
          const select = optionContainer.querySelector(
            "select" + selectors.optionSelector,
          );
          if (!select) return;

          const optionPosition = `option${index + 1}`;
          const options = select.querySelectorAll("option");

          options.forEach((option) => {
            const value = option.value;
            const state = this.checkOptionAvailability(
              value,
              optionPosition,
              currentSelections,
            );

            option.dataset.availabilityState = state;

            // Update option text
            const baseText = value;
            if (state === states.SOLD_OUT) {
              option.textContent = `${baseText} - ${window.themeVariables?.strings?.soldOutButton || "Sold Out"}`;
              option.disabled = true;
            } else if (state === states.UNAVAILABLE) {
              option.textContent = `${baseText} - Unavailable`;
              option.disabled = true;
            } else {
              option.textContent = baseText;
              option.disabled = false;
            }
          });
        });
    }

    updateSwatches(currentSelections) {
      this.container.querySelectorAll(selectors.swatch).forEach((swatch) => {
        const optionPosition = swatch.dataset.optionPosition;
        const value = swatch.value;
        const state = this.checkOptionAvailability(
          value,
          optionPosition,
          currentSelections,
        );

        swatch.dataset.availabilityState = state;

        const swatchElement = swatch.nextElementSibling;
        if (
          !swatchElement ||
          !swatchElement.classList.contains("swatch-element")
        )
          return;

        // Remove all state classes
        swatchElement.classList.remove(
          classes.swatchSoldOut,
          classes.swatchUnavailable,
        );

        // Remove existing unavailable mark
        const existingMark = swatchElement.querySelector(
          ".swatch-unavailable-mark",
        );
        if (existingMark) {
          existingMark.remove();
        }

        // Add state-specific styling
        if (state === states.SOLD_OUT) {
          swatchElement.classList.add(classes.swatchSoldOut);
          const label = swatchElement.querySelector("label");
          if (label) {
            const mark = document.createElement("span");
            mark.className = "swatch-unavailable-mark";
            label.appendChild(mark);
          }
        } else if (state === states.UNAVAILABLE) {
          swatchElement.classList.add(classes.swatchUnavailable);
          const label = swatchElement.querySelector("label");
          if (label) {
            const mark = document.createElement("span");
            mark.className = "swatch-unavailable-mark";
            label.appendChild(mark);
          }
        }

        // Update tooltip
        this.updateSwatchTooltip(swatchElement, value, state);
      });
    }

    updateGridSwatches(currentSelections) {
      this.container
        .querySelectorAll(selectors.swatchGrid)
        .forEach((swatch) => {
          const optionPosition = swatch.dataset.optionPosition;
          const value = swatch.value;
          const state = this.checkOptionAvailability(
            value,
            optionPosition,
            currentSelections,
          );

          swatch.dataset.availabilityState = state;

          const swatchElement = swatch.nextElementSibling;
          if (
            !swatchElement ||
            !swatchElement.classList.contains("swatch-element")
          )
            return;

          // Remove all state classes
          swatchElement.classList.remove(
            classes.swatchSoldOut,
            classes.swatchUnavailable,
          );

          // Remove existing unavailable mark
          const existingMark = swatchElement.querySelector(
            ".swatch-unavailable-mark",
          );
          if (existingMark) {
            existingMark.remove();
          }

          // Add state-specific styling
          if (state === states.SOLD_OUT) {
            swatchElement.classList.add(classes.swatchSoldOut);
            const label = swatchElement.querySelector("label");
            if (label) {
              const mark = document.createElement("span");
              mark.className = "swatch-unavailable-mark";
              label.appendChild(mark);
            }
          } else if (state === states.UNAVAILABLE) {
            swatchElement.classList.add(classes.swatchUnavailable);
            const label = swatchElement.querySelector("label");
            if (label) {
              const mark = document.createElement("span");
              mark.className = "swatch-unavailable-mark";
              label.appendChild(mark);
            }
          }

          // Update tooltip
          this.updateSwatchTooltip(swatchElement, value, state);
        });
    }

    updateSwatchTooltip(swatchElement, value, state) {
      const tooltip = swatchElement.querySelector(".tooltip");
      if (!tooltip) return;

      let content = value;

      if (state === states.SOLD_OUT) {
        content +=
          "<br><small>(" +
          (window.themeVariables?.strings?.soldOutButton || "Sold Out") +
          ")</small>";
      } else if (state === states.UNAVAILABLE) {
        content += "<br><small>(Unavailable)</small>";
      }

      tooltip.innerHTML = content;
    }
  }

  // Initialize on product sections
  function init() {
    const productSections = document.querySelectorAll(selectors.productSection);

    productSections.forEach((section) => {
      if (!section.hasAttribute("data-variant-availability-initialized")) {
        new VariantAvailability(section);
        section.setAttribute("data-variant-availability-initialized", "true");
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Handle quick view and other dynamic content
  document.addEventListener("theme:addon:quick-view:loaded", () => {
    setTimeout(init, 100);
  });

  // Handle Shopify section loads
  document.addEventListener("shopify:section:load", () => {
    init();
  });
})();
